import { GLOBAL_FALLBACK_UUID, Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { ASSEMBLY_AI_MULTILINGUAL_STREAMING_SPEECH_MODEL } from "@/modules/audio-recording/assembly-ai-constants";
import { electronModule } from "@/modules/electron";
import { uuidModule } from "@/modules/uuid";
import {
  AudioRecordingError,
  AudioRecordingErrorSource,
  AudioRecordingErrorStage,
} from "@/domains/errors";
import {
  isDesktopRecordingStartFailureError,
  isNetworkRelatedRecordingError,
} from "@/store/audio/recording/media-controller/desktop-controller-errors";
import {
  PrepareRecordingArgs,
  PreparedRecording,
  PreparedRecordingController,
  RecallUploadCredentials,
} from "@/store/audio/recording/prepared-recording-controller/types";
import { DateTime } from "luxon";

interface RecallSdkUploadResponse {
  upload_token: string;
  sdk_upload_id?: string;
}

const NETWORK_RETRY_DELAYS_MS = [1_000, 2_000];

/** Retries connectivity failures so a transient network blip doesn't kill the
 *  recording start. Restart-required errors propagate untouched — the store
 *  handles them with a whole-intent rebuild. */
async function retryOnNetworkError<T>(operation: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= NETWORK_RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await new Promise(resolve => setTimeout(resolve, NETWORK_RETRY_DELAYS_MS[attempt - 1]));
    }
    try {
      return await operation();
    } catch (error) {
      if (isDesktopRecordingStartFailureError(error) || !isNetworkRelatedRecordingError(error)) {
        throw error;
      }
      lastError = error;
    }
  }
  throw lastError;
}

/** Marks a prewarmed promise as handled so an abandoned (never-awaited)
 *  prepared recording can't surface an unhandled rejection; consumers that do
 *  await it still observe the rejection. */
function guardAbandonedRejection<T>(promise: Promise<T>): Promise<T> {
  void promise.catch(() => {});
  return promise;
}

export class DesktopPreparedRecordingController implements PreparedRecordingController {
  prepareRecording({
    customVocabulary,
    audioRecordingId,
  }: PrepareRecordingArgs): PreparedRecording {
    return {
      audioRecordingId,
      customVocabulary,
      wholeDesktopWindowId: guardAbandonedRejection(this.fetchWholeDesktopWindowId()),
      recallUploadCredentials: guardAbandonedRejection(
        this.fetchRecallUploadCredentials(audioRecordingId, customVocabulary)
      ),
      // After 3 hours, @recallai/desktop-sdk startRecording needs to do a network
      // request to confirm it, taking 0.5~1 second.
      expiresAt: DateTime.local().plus({ hours: 2, minutes: 30 }),
    };
  }

  private async fetchWholeDesktopWindowId(): Promise<string> {
    try {
      const { windowId } = await retryOnNetworkError(() =>
        electronModule.recall.prepareDesktopRecording()
      );
      return windowId;
    } catch (error) {
      throw new AudioRecordingError({
        source: AudioRecordingErrorSource.RecallDesktop,
        stage: AudioRecordingErrorStage.PrepareWindow,
        message: error instanceof Error ? error.message : String(error),
        originalError: error instanceof Error ? error : undefined,
        details: {
          action: "prepareDesktopRecording",
        },
      });
    }
  }

  private async fetchRecallUploadCredentials(
    audioRecordingId: Uuid,
    customVocabulary: string[] | null
  ): Promise<RecallUploadCredentials> {
    const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
      associatedAudioRecordingId: audioRecordingId,
    });
    let response;
    try {
      response = await retryOnNetworkError(() =>
        api.post("/v2/voice/recall_sdk_upload", {
          body: {
            // This is not being used by the backend.
            note_id: GLOBAL_FALLBACK_UUID,
            captured_audio_id: capturedAudioId,
            recording_config: {
              transcript: {
                provider: {
                  assembly_ai_v3_streaming: {
                    format_turns: true,
                    speech_model: ASSEMBLY_AI_MULTILINGUAL_STREAMING_SPEECH_MODEL,
                    language_detection: true,

                    keyterms_prompt: customVocabulary?.length
                      ? JSON.stringify(customVocabulary)
                      : null,
                  },
                },
              },
              realtime_endpoints: [
                {
                  type: "desktop-sdk-callback",
                  events: [
                    // join + update carry participant identity. update can
                    // backfill names/emails not present at join time.
                    "participant_events.join",
                    "participant_events.update",
                    "transcript.data",
                    "transcript.partial_data",
                    "audio_mixed_raw.data",
                  ],
                },
              ],
              // System audio only
              video_mixed_mp4: null,
              audio_mixed_mp3: {},
            },
          },
        })
      );
    } catch (error) {
      throw new AudioRecordingError({
        source: AudioRecordingErrorSource.RecallDesktop,
        stage: AudioRecordingErrorStage.UploadCredentials,
        message: error instanceof Error ? error.message : String(error),
        originalError: error instanceof Error ? error : undefined,
        details: {
          endpoint: "/v2/voice/recall_sdk_upload",
        },
      });
    }
    const data = response.data as RecallSdkUploadResponse | undefined;
    if (!data?.upload_token) {
      throw new AudioRecordingError({
        source: AudioRecordingErrorSource.RecallDesktop,
        stage: AudioRecordingErrorStage.UploadCredentials,
        message: "Recall upload token missing in response.",
        details: {
          endpoint: "/v2/voice/recall_sdk_upload",
        },
      });
    }
    return {
      uploadToken: data.upload_token,
      sdkUploadId: data.sdk_upload_id ?? null,
    };
  }
}
